"use client";
import React, { useState } from "react";
import { FaRocket, FaCode, FaMobileAlt, FaBrain, FaShoppingCart, FaCloud, FaCheck, FaArrowRight, FaArrowLeft, FaPaperPlane, FaServer } from "react-icons/fa";

const GetStarted = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    projectTypes: [] as string[],
    industry: "",
    features: [] as string[],
    aiFeatures: [] as string[],
    budget: "",
    timeline: "",
    name: "",
    email: "",
    company: "",
    description: "",
  });

  const projectTypes = [
    { id: "webapp", icon: FaCode, label: "Web Application", desc: "Custom web apps & platforms" },
    { id: "mobile", icon: FaMobileAlt, label: "Mobile App", desc: "iOS & Android applications" },
    { id: "api", icon: FaServer, label: "API Development", desc: "RESTful & GraphQL APIs" },
    { id: "ai", icon: FaBrain, label: "AI Solution", desc: "AI/ML integration & agents" },
    { id: "pos", icon: FaShoppingCart, label: "BMS System", desc: "Business management solutions" },
    { id: "cloud", icon: FaCloud, label: "Cloud Infrastructure", desc: "Cloud architecture & DevOps" },
  ];

  const industries = ["Retail & E-commerce", "Healthcare", "Finance & Banking", "Education", "Logistics", "Manufacturing", "Real Estate", "Food & Restaurant", "Other"];

  const features = {
    webapp: ["User Authentication", "Payment Integration", "Admin Dashboard", "Real-time Updates", "API Development", "Database Design", "Search & Filtering", "Notifications"],
    mobile: ["Push Notifications", "Offline Mode", "Camera Integration", "GPS & Maps", "Social Login", "In-App Purchases", "Biometric Auth", "Chat/Messaging"],
    api: ["RESTful API", "GraphQL", "Authentication", "Rate Limiting", "Documentation", "Webhooks", "Versioning", "Caching"],
    ai: ["Chatbot/Assistant", "Data Analytics", "Predictive Models", "Image Recognition", "NLP Processing", "Recommendation Engine", "Automation", "Voice Integration"],
    pos: ["Inventory Management", "Multi-location", "Employee Management", "Reporting", "Customer Loyalty", "Payment Links", "AI Tools", "Online Orders"],
    cloud: ["Auto-scaling", "Load Balancing", "CI/CD Pipeline", "Monitoring", "Security", "Backup & Recovery", "Microservices", "Containerization"],
  };

  const aiEnhancements = ["AI-Powered Search", "Smart Recommendations", "Automated Reports", "Predictive Analytics", "Natural Language Interface", "Intelligent Automation", "Anomaly Detection", "Personalization Engine"];

  const budgets = ["$2,000 - $5,000", "$5,000 - $15,000", "$15,000 - $30,000", "$30,000 - $50,000", "$50,000 - $100,000", "$100,000+"];
  const timelines = ["1-2 months", "2-4 months", "4-6 months", "6+ months", "Flexible"];

  const toggleProjectType = (typeId: string) => {
    setFormData((prev) => ({
      ...prev,
      projectTypes: prev.projectTypes.includes(typeId) 
        ? prev.projectTypes.filter((t) => t !== typeId) 
        : [...prev.projectTypes, typeId],
    }));
  };

  const toggleFeature = (feature: string, type: "features" | "aiFeatures") => {
    setFormData((prev) => ({
      ...prev,
      [type]: prev[type].includes(feature) ? prev[type].filter((f) => f !== feature) : [...prev[type], feature],
    }));
  };

  const getAllFeatures = () => {
    const allFeatures = new Set<string>();
    formData.projectTypes.forEach((type) => {
      (features[type as keyof typeof features] || []).forEach((f) => allFeatures.add(f));
    });
    return Array.from(allFeatures);
  };

  const nextStep = () => setStep((s) => Math.min(s + 1, 5));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = () => {
    console.log("Form submitted:", formData);
    setStep(6);
  };

  return (
    <div id="getstarted-page" className="min-h-screen pt-32 md:pt-40 pb-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-grid opacity-20"></div>
      <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-kuttos-cyan/5 rounded-full blur-[150px]"></div>
      <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-kuttos-cyan/5 rounded-full blur-[120px]"></div>

      {/* Light Beams */}
      <div className="absolute top-0 left-1/4 w-px h-96 bg-gradient-to-b from-kuttos-cyan/30 via-kuttos-cyan/10 to-transparent"></div>
      <div className="absolute top-0 right-1/3 w-px h-80 bg-gradient-to-b from-kuttos-cyan/20 via-kuttos-cyan/5 to-transparent"></div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-kuttos-cyan/10 border border-kuttos-cyan/30 mb-6">
            <FaRocket className="text-kuttos-cyan" />
            <span className="text-kuttos-cyan text-sm font-semibold">Project Builder</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">Let's Build Something Amazing</h1>
          <p className="text-gray-400 text-lg">Tell us about your vision and we'll bring it to life</p>
        </div>

        {/* Progress Bar */}
        {step < 6 && (
          <div className="mb-12">
            <div className="flex justify-between mb-2">
              {["Project", "Industry", "Features", "AI", "Details"].map((label, i) => (
                <span key={i} className={`text-xs font-medium ${step > i ? "text-kuttos-cyan" : "text-gray-600"}`}>{label}</span>
              ))}
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-kuttos-cyan transition-all duration-500" style={{ width: `${(step / 5) * 100}%` }}></div>
            </div>
          </div>
        )}

        {/* Step 1: Project Type - Multiple Selection */}
        {step === 1 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold text-white mb-2 text-center">What would you like to build?</h2>
            <p className="text-gray-500 text-center mb-8">Select all that apply</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => toggleProjectType(type.id)}
                  className={`p-6 rounded-xl border transition-all duration-300 text-left group relative ${
                    formData.projectTypes.includes(type.id)
                      ? "bg-kuttos-cyan/10 border-kuttos-cyan"
                      : "bg-gray-900/50 border-gray-800 hover:border-gray-700"
                  }`}
                >
                  {formData.projectTypes.includes(type.id) && (
                    <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-kuttos-cyan flex items-center justify-center">
                      <FaCheck className="text-black text-xs" />
                    </div>
                  )}
                  <type.icon className={`text-3xl mb-4 ${formData.projectTypes.includes(type.id) ? "text-kuttos-cyan" : "text-gray-500 group-hover:text-gray-400"}`} />
                  <h3 className="text-white font-bold mb-1">{type.label}</h3>
                  <p className="text-gray-500 text-sm">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Industry */}
        {step === 2 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">What industry are you in?</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {industries.map((industry) => (
                <button
                  key={industry}
                  onClick={() => setFormData({ ...formData, industry })}
                  className={`p-4 rounded-xl border transition-all duration-300 ${
                    formData.industry === industry
                      ? "bg-kuttos-cyan/10 border-kuttos-cyan text-kuttos-cyan"
                      : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                  }`}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Features */}
        {step === 3 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Select the features you need</h2>
            <p className="text-gray-500 text-center mb-8">Choose all that apply</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {getAllFeatures().map((feature) => (
                <button
                  key={feature}
                  onClick={() => toggleFeature(feature, "features")}
                  className={`p-3 rounded-lg border transition-all duration-300 text-sm flex items-center gap-2 ${
                    formData.features.includes(feature)
                      ? "bg-kuttos-cyan/10 border-kuttos-cyan text-kuttos-cyan"
                      : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                  }`}
                >
                  {formData.features.includes(feature) && <FaCheck className="text-xs" />}
                  {feature}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: AI Enhancements */}
        {step === 4 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Add AI-Powered Features?</h2>
            <p className="text-gray-500 text-center mb-8">Supercharge your project with AI</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {aiEnhancements.map((feature) => (
                <button
                  key={feature}
                  onClick={() => toggleFeature(feature, "aiFeatures")}
                  className={`p-3 rounded-lg border transition-all duration-300 text-sm flex items-center gap-2 ${
                    formData.aiFeatures.includes(feature)
                      ? "bg-kuttos-cyan/10 border-kuttos-cyan text-kuttos-cyan"
                      : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                  }`}
                >
                  {formData.aiFeatures.includes(feature) && <FaCheck className="text-xs" />}
                  {feature}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Contact Details */}
        {step === 5 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Almost there! Tell us about yourself</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Your Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-kuttos-cyan"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-kuttos-cyan"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Company Name</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-kuttos-cyan"
                  placeholder="Acme Inc."
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Budget Range</label>
                <select
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-kuttos-cyan"
                >
                  <option value="">Select budget</option>
                  {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-6">
              <label className="text-gray-400 text-sm mb-2 block">Timeline</label>
              <div className="flex flex-wrap gap-2">
                {timelines.map((t) => (
                  <button
                    key={t}
                    onClick={() => setFormData({ ...formData, timeline: t })}
                    className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                      formData.timeline === t
                        ? "bg-kuttos-cyan/10 border-kuttos-cyan text-kuttos-cyan"
                        : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Project Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-kuttos-cyan resize-none"
                placeholder="Tell us about your project vision, goals, and any specific requirements..."
              />
            </div>
          </div>
        )}

        {/* Step 6: Success */}
        {step === 6 && (
          <div className="animate-scale-in text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-kuttos-cyan to-kuttos-blue flex items-center justify-center">
              <FaCheck className="text-3xl text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Thank You!</h2>
            <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
              We've received your project details. Our team will review your requirements and get back to you within 24-48 hours with a detailed proposal.
            </p>
            <p className="text-gray-500 mb-8">
              Contact us directly: <a href="mailto:contact@kuttostechnologies.com" className="text-kuttos-cyan hover:underline">contact@kuttostechnologies.com</a>
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        {step < 6 && (
          <div className="flex justify-between mt-12">
            <button
              onClick={prevStep}
              className={`flex items-center gap-2 px-6 py-3 rounded-full border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600 transition-all ${step === 1 ? "invisible" : ""}`}
            >
              <FaArrowLeft /> Back
            </button>
            {step < 5 ? (
              <button
                onClick={nextStep}
                disabled={step === 1 && formData.projectTypes.length === 0}
                className="flex items-center gap-2 px-8 py-3 rounded-full bg-kuttos-cyan text-black font-bold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue <FaArrowRight />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!formData.name || !formData.email || !formData.description}
                className="flex items-center gap-2 px-8 py-3 rounded-full bg-kuttos-cyan text-black font-bold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Project <FaPaperPlane />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GetStarted;
